/**
 * SmartWishlist SDK Ready Handler
 * Add promise control
 */
class SmartWishlistSDK {
  static readyPromise = null;
  static resolveReady = null;
  static ready = false;

  static {
    this.readyPromise = new Promise((resolve) => {
      this.resolveReady = resolve;
    });

    if (typeof window.SmartWishlistMain === 'function') {
      this.markAsReady();
    } else {
      window.addEventListener('smartwishlist:ready', () => this.markAsReady(), { once: true });
      this.observeScriptLoad();
    }
  }

  static markAsReady() {
    if (this.ready) return;
    this.ready = true;
    this.resolveReady();
  }

  static observeScriptLoad() {
    let smartWishlistMain = window.SmartWishlistMain;
    
    Object.defineProperty(window, 'SmartWishlistMain', {
      get: () => smartWishlistMain,
      set: (value) => {
        smartWishlistMain = value;
        if (typeof value === 'function') {
          this.markAsReady();
        }
      },
      configurable: true
    });
  }

  /**
   * return promise that it resolve when SDK is ready
   * @returns {Promise<void>}
   */
  static whenReady() {
    return this.readyPromise;
  }

  /**
   * @returns {boolean}
   */
  static get isReady() {
    return this.ready;
  }
}

class PoWishlistUtils {
    static mergePromise = null;

    static get metaStorageKey() {
        return `${WISHLIST_CONSTANTS.localStorageKey}_meta`;
    }

    static get pendingMergeKey() {
        return `${WISHLIST_CONSTANTS.localStorageKey}_pending`;
    }

    static get customerId() {
        return ShopifyAnalytics?.meta?.page?.customerId ? String(ShopifyAnalytics.meta.page.customerId) : null;
    }

    static get sessionMeta() {
        try {
            return JSON.parse(localStorage.getItem(this.metaStorageKey)) || null;
        } catch (e) {
            return null;
        }
    }

    static set sessionMeta(meta) {
        try {
            if (meta) {
                localStorage.setItem(this.metaStorageKey, JSON.stringify(meta));
            } else {
                localStorage.removeItem(this.metaStorageKey);
            }
        } catch (e) {}
    }

    static get pendingMerge() {
        try {
            return JSON.parse(localStorage.getItem(this.pendingMergeKey)) || null;
        } catch (e) {
            return null;
        }
    }

    static set pendingMerge(data) {
        try {
            if (data) {
                localStorage.setItem(this.pendingMergeKey, JSON.stringify(data));
            } else {
                localStorage.removeItem(this.pendingMergeKey);
            }
        } catch (e) {}
    }

    static clearLocalWishlist() {
        try { localStorage.removeItem(WISHLIST_CONSTANTS.localStorageKey); } catch (e) {}
    }

    static clearWishlistCookie() {
        try {
            document.cookie = 'wishlist_id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        } catch (e) {}
    }

    static initSessionSync() {
        try {
            this.handleSessionTransition();
        } catch (e) {
            console.error('[Wishlist] Session sync failed:', e);
        }
    }

    static handleSessionTransition() {
        const customerId = this.customerId;
        const meta = this.sessionMeta;
        const pending = this.pendingMerge;
        const cached = this.cachedWishlist || [];

        if (!customerId) {
            if (meta?.ownerType === 'customer' || pending) {
                this.clearLocalWishlist();
                this.pendingMerge = null;
                this.clearWishlistCookie();
                publish(PUB_SUB_EVENTS.updateWishlist, { wishlist: [] });
            }
            if (!meta || meta.ownerType !== 'guest') {
                this.sessionMeta = {
                    ownerType: 'guest',
                    customerId: null,
                    lastCustomerId: meta?.customerId || meta?.lastCustomerId || null
                };
            }
            return;
        }

        if (meta?.lastCustomerId && meta.lastCustomerId !== customerId) {
            this.clearLocalWishlist();
            this.pendingMerge = null;
        }

        if (meta?.ownerType === 'customer' && meta.customerId && meta.customerId !== customerId) {
            this.clearLocalWishlist();
        }

        let itemsToMerge = [];

        if (pending?.customerId === customerId) {
            itemsToMerge = pending.items || [];
        } else if (cached.length > 0 && meta?.ownerType !== 'customer') {
            itemsToMerge = cached;
            this.pendingMerge = {
                customerId,
                items: itemsToMerge,
                createdAt: new Date().toISOString()
            };
        } else if (pending && pending.customerId !== customerId) {
            this.pendingMerge = null;
        }

        this.sessionMeta = { ownerType: 'customer', customerId, lastCustomerId: customerId };

        if (itemsToMerge.length) {
            this.startMerge(itemsToMerge);
        }
    }

    static normalizeWishlistItem(item) {
        return {
            productId: item?.product_id || item?.productId || null,
            variantId: item?.variant_id || item?.variantId || null,
            productHandle: item?.handle || item?.productHandle || null
        };
    }

    static startMerge(items) {
        if (this.mergePromise) return this.mergePromise;
        this.mergePromise = (async () => {
            try {
                await SmartWishlistSDK.whenReady();
                const existing = await this.fetchWishlist();
                const existingIds = new Set(existing.map(item => String(item.variant_id)));
                for (const rawItem of items) {
                    const wlItem = this.normalizeWishlistItem(rawItem);
                    if (!wlItem.productId || !wlItem.variantId) continue;
                    if (existingIds.has(String(wlItem.variantId))) continue;
                    await this.callWishlistAPI(WISHLIST_CONSTANTS.eventKeyAdd, wlItem);
                }
                this.pendingMerge = null;
                const updated = await this.fetchWishlist();
                publish(PUB_SUB_EVENTS.updateWishlist, { wishlist: updated });
                this.clearWishlistCookie();
            } catch (error) {
                console.error('[Wishlist] Merge failed:', error);
            } finally {
                this.mergePromise = null;
            }
        })();
        return this.mergePromise;
    }
    /**
     * @returns {Array|null} 
     */
    static get cachedWishlist() {
        const wl = localStorage.getItem(WISHLIST_CONSTANTS.localStorageKey) || null;
        return wl && JSON.parse(wl);
    }
    
    static set cachedWishlist(wl) {
        try {
            if (wl === null || wl === undefined) {
                localStorage.removeItem(WISHLIST_CONSTANTS.localStorageKey);
                return;
            }
            localStorage.setItem(WISHLIST_CONSTANTS.localStorageKey, JSON.stringify(wl));
        } catch (e) {}
    }

    /**
     * @returns {String} 
     */
    static get wishlistUrl() {
        const customerId = ShopifyAnalytics.meta.page.customerId;
        const baseUrl = `${Shopify.routes.root}a/wishlist?type=api&version=1`;
        return customerId
      ? `${baseUrl}&customerid=${customerId}`
      : `${baseUrl}&wishlistid=${this.wishlistId}`;
    }

    /**
     * @returns {String|null} 
     */
    static get wishlistId() {
        const cookies = decodeURIComponent(document.cookie).split(';');
        const wishlistCookie = cookies.find(c => c.trim().startsWith('wishlist_id='));
        return wishlistCookie?.trim().substring('wishlist_id='.length) || null;
    }

    /**
     * Looks for the cached wishlist object if available, else fetch the new one
     * @returns {Promise<Array>}
     */
    static async retrieveWishlist() {
        this.initSessionSync();
        const cached = this.cachedWishlist;
        if (cached !== null && cached !== undefined) return cached;
        return this.fetchWishlist();
    }


    /**
     * Fetch from server
     * @returns {Promise<Array>}
     */
    static async fetchWishlist() {

        if (!this.wishlistId && !ShopifyAnalytics.meta.page.customerId) {
            console.warn('[Wishlist] No wishlist ID or customer ID — skipping fetch.');
            return [];
        }

        try {
            const response = await fetch(this.wishlistUrl);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const { items: wlItems = [] } = await response.json();
            localStorage.setItem(WISHLIST_CONSTANTS.localStorageKey, JSON.stringify(wlItems));
            return wlItems;
        } catch (error) {
            console.error('[Wishlist] Error fetching:', error);
            return this.cachedWishlist || [];
        }
    }


    /**
     * check if variant is in wishlist page
     * @param {string} variantId
     * @returns {Promise<boolean>}
     */
    static async isItemInWishlist(variantId) {
        const wl = await this.retrieveWishlist();
        return wl.some(item => item.variant_id === variantId);
    }

    /**
     * update wishlist (add/remove)
     * 
     * @param {string} eventKey - one of the keys in WISHLIST_CONSTANTS.
     * @param {Object} item - The item to add to the wishlist.
     * @param {string} item.productId - The product's unique ID.
     * @param {string?} [item.productHandle] - The product's handle/slug.
     * @param {string} item.variantId - The variant's unique ID.
     * @returns {Promise<Array>}
     */
    static async updateWishlist(eventKey, item) {
        this.sessionMeta = {
            ownerType: this.customerId ? 'customer' : 'guest',
            customerId: this.customerId
        };
        const pending = this.pendingMerge;
        if (pending?.customerId === this.customerId && Array.isArray(pending.items)) {
            const variantId = String(item?.variantId || '');
            const filtered = pending.items.filter(p => String(p?.variant_id || p?.variantId || '') !== variantId);
            if (filtered.length !== pending.items.length) {
                this.pendingMerge = filtered.length ? { ...pending, items: filtered } : null;
            }
        }
        const currentWl = this.applyOptimisticUpdate(eventKey, item);
        publish(PUB_SUB_EVENTS.updateWishlist, { wishlist: currentWl });

        this.callWishlistAPI(eventKey, item).catch(error => {
        console.error('[Wishlist] API call failed:', error);
        });
        
        return currentWl;
    }

    /**
     * apply optimistic update to local cache
     */
    static applyOptimisticUpdate(eventKey, item) {
        let currentWl = this.cachedWishlist || [];

        if (eventKey === WISHLIST_CONSTANTS.eventKeyAdd) {
        const exists = currentWl.some(wlItem => wlItem.variant_id === item.variantId);
        
        if (!exists) {
            currentWl = [...currentWl, {
            product_id: item.productId,
            variant_id: item.variantId,
            handle: item.productHandle
            }];
        }
        } else if (eventKey === WISHLIST_CONSTANTS.eventKeyRemove) {
        currentWl = currentWl.filter(wlItem => wlItem.variant_id !== item.variantId);
        }

        localStorage.setItem(WISHLIST_CONSTANTS.localStorageKey, JSON.stringify(currentWl));
        return currentWl;
    }

    /**
     * call 'API for SmartWishlist
     */
    static async callWishlistAPI(eventKey, item) {
        await SmartWishlistSDK.whenReady();

        const apiMap = {
        [WISHLIST_CONSTANTS.eventKeyAdd]: 'AddToSmartWishlist',
        [WISHLIST_CONSTANTS.eventKeyRemove]: 'RemoveFromSmartWishlist'
        };

        const fnName = apiMap[eventKey];
        
        if (fnName && typeof window[fnName] === 'function') {
        await window[fnName](item.productId, item.variantId);
        }
    }
}

class PoWishlist extends HTMLElement {
    constructor() {
        super();
        this.customerId = ShopifyAnalytics.meta.page.customerId || null;
        this.wishlistData = [];
        this.unsubscribe = null;
    }

    get loader() {
        return this.querySelector('[data-wishlist-loader]');
    }

    get itemCountDisplay() {
        return this.querySelector('[data-wishlist-item-count]');
    }

    async connectedCallback() {
        await this.loadWishlist();
        
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.updateWishlist, () => {
            this.loadWishlist();
        });
    }

    disconnectedCallback() {
        this.unsubscribe?.();
    }

    async loadWishlist() {
        this.wishlistData = await PoWishlistUtils.retrieveWishlist();
        await this.renderWishlistItems();
    }

    hideLoader() {
        if (this.loader) {
            this.loader.style.display = 'none';
        }
    }

    async renderWishlistItems() {
        const emptyWrapper = this.querySelector('#wishlist-empty-wrapper');
        const productsWrapper = this.querySelector('#wishlist-products-wrapper');
        const container = this.querySelector('#wishlist-products');

        this.updateItemCount();

        if (this.wishlistData.length === 0) {
        emptyWrapper?.classList.remove('hidden');
        productsWrapper?.classList.add('hidden');
        this.hideLoader();
        return;
        }

        try {
        const fragment = await this.fetchWishlistItemsHTML();
        
        if (container) {
            container.innerHTML = '';
            container.appendChild(fragment);
            this.handleLoginCard(container);
        }

        emptyWrapper?.classList.add('hidden');
        productsWrapper?.classList.remove('hidden');
        } catch (error) {
        console.error('[Wishlist] Error loading items:', error);
        }

        this.hideLoader();
    }

    updateItemCount() {
        if (!this.itemCountDisplay) return;
        
        const count = this.wishlistData.length;
        this.itemCountDisplay.textContent = count > 0 ? `(${count})` : '';
        this.itemCountDisplay.toggleAttribute('data-visible', count > 0);
    }

    async fetchWishlistItemsHTML() {
        const fetchPromises = this.wishlistData.map(item =>
        fetch(`${Shopify.routes.root}products/${item.handle}/?sections=po-wishlist-item&variant=${item.variant_id}`)
        );

        const responses = await Promise.all(fetchPromises);
        const jsonResponses = await Promise.all(responses.map(res => res.json()));
        
        const fragment = document.createDocumentFragment();
        
        jsonResponses.forEach(response => {
        const html = new DOMParser().parseFromString(response['po-wishlist-item'], 'text/html');
        const wishlistItem = html.querySelector('[data-po-wishlist-item]');
        
        if (wishlistItem) {
            fragment.appendChild(wishlistItem);
        }
        });

        return fragment;
    }

    handleLoginCard(container) {
        if (!container || this.customerId) return;

        container.querySelector('.wishlist-login-card')?.remove();

        const loginCard = this.createLoginCard();
        if (!loginCard) return;

        const productItems = container.querySelectorAll('[data-po-wishlist-item]');
        const insertAfterIndex = Math.min(productItems.length, 2) - 1;
        
        if (insertAfterIndex >= 0) {
        productItems[insertAfterIndex].insertAdjacentElement('afterend', loginCard);
        } else {
        container.appendChild(loginCard);
        }

        this.initLoginCardModal(loginCard);
    }

    createLoginCard() {
        const template = document.getElementById('wishlist-login-template');
        if (!template) return null;

        const card = document.createElement('div');
        card.className = 'wishlist-login-card';
        card.innerHTML = template.innerHTML;
        
        return card;
    }

    initLoginCardModal(cardEl) {
        const loginModal = document.querySelector('#login-overlay-modal');
        if (!loginModal || typeof loginModal.showModal !== 'function') return;

        cardEl.querySelectorAll('[data-login-trigger]').forEach(trigger => {
        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            
            if (loginModal.dataset.position === 'right') {
            loginModal.classList.add('tw-animate-slide-left');
            }
            
            loginModal.showModal();
        });
        });
    }
}

if (!customElements.get('po-wishlist')) {
    customElements.define('po-wishlist', PoWishlist);
}

class PoWishlistToggleButton extends HTMLElement {
    constructor() {
        super();
        this.wishlistItem = {
            productId: this.dataset.productId,
            variantId: this.dataset.variantId,
            productHandle: this.dataset.handle,
            variantSku: this.dataset.sku || this.dataset.variantSku || null
        };
        this.isInWishlist = false;
        this.unsubscribers = [];
    }

    async connectedCallback() {
        await this.init();
    }

    disconnectedCallback() {
        this.unsubscribers.forEach(unsub => unsub?.());
    }

    async init() {
        await this.syncWishlist();
        await this.enableWhenSDKReady();
        this.setupSubscriptions();
        this.setupEventListeners();
    }

    async enableWhenSDKReady() {
        await SmartWishlistSDK.whenReady();
        this.removeAttribute('disabled');
    }

    setupSubscriptions() {
        this.unsubscribers.push(
        subscribe(PUB_SUB_EVENTS.updateWishlist, () => this.checkWishlistItemPresence()),
        subscribe(PUB_SUB_EVENTS.variantChange, (payload) => this.handleVariantChange(payload))
        );
    }

    setupEventListeners() {
        this.addEventListener('click', this.handleClick.bind(this));
        this.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    handleVariantChange(payload) {
        const { productId, variant } = payload.data;
        
        if (this.wishlistItem.productId !== productId) return;
        if (this.wishlistItem.variantId === String(variant.id)) return;

        this.wishlistItem.variantId = String(variant.id);
        this.wishlistItem.variantSku = variant?.sku || null;
        this.checkWishlistItemPresence();
    }

    syncAriaPressed() {
        // Only the icon-only variant declares aria-pressed in its markup; the cart variant
        // exposes its state through visible text and is left untouched.
        if (!this.hasAttribute('aria-pressed')) return;
        this.setAttribute('aria-pressed', this.hasAttribute('active') ? 'true' : 'false');
    }

    handleClick(evt) {
        evt.preventDefault();
        this.toggleAttribute('active');
        this.syncAriaPressed();

        const eventKey = this.isInWishlist
            ? WISHLIST_CONSTANTS.eventKeyRemove
            : WISHLIST_CONSTANTS.eventKeyAdd;

        this.sendTealiumTracking(eventKey);

        PoWishlistUtils.updateWishlist(eventKey, this.wishlistItem);
    }

    getDirectTealiumProductKeyCandidate() {
        return this.wishlistItem.variantSku || this.wishlistItem.variantId || this.wishlistItem.productId;
    }

    getTealiumProductKey() {
        const tealiumProductEntry = this.getTealiumProductEntry();
        if (tealiumProductEntry) {
            return tealiumProductEntry.key;
        }

        return this.getDirectTealiumProductKeyCandidate();
    }

    getDatasetTealiumProductData() {
        if (!this.dataset.trackingProduct) {
            return null;
        }

        try {
            return JSON.parse(this.dataset.trackingProduct);
        } catch (error) {
            return null;
        }
    }

    normalizeTealiumCategory(value) {
        const normalizedValue = String(value || '')
            .trim()
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .toLowerCase();

        const categoryMap = {
            accessories: 'ACCESSORIES',
            accessory: 'ACCESSORIES',
            optics: 'OPTICS',
            optical: 'OPTICS',
            eyeglasses: 'OPTICS',
            eyeglass: 'OPTICS',
            sun: 'SUN',
            sunglasses: 'SUN',
            sunglass: 'SUN',
            afa: 'AFA',
            eyewear: 'EYEWEAR',
            electronics: 'Electronics',
            electronic: 'Electronics',
            giftcard: 'GIFTCARD',
            'gift card': 'GIFTCARD',
            'gift cards': 'GIFTCARD',
            'contact lens': 'CONTACT LENSES',
            'contact lenses': 'CONTACT LENSES',
            vas: 'VAS',
            service: 'VAS',
            services: 'VAS',
            'value added service': 'VAS',
            'value added services': 'VAS',
        };

        if (categoryMap[normalizedValue]) {
            return categoryMap[normalizedValue];
        }

        if (normalizedValue.includes('contact lens')) return 'CONTACT LENSES';
        if (normalizedValue.includes('gift card')) return 'GIFTCARD';
        if (normalizedValue.includes('electronic')) return 'Electronics';
        if (normalizedValue.includes('accessor')) return 'ACCESSORIES';
        if (normalizedValue.includes('optic') || normalizedValue.includes('eyeglass')) return 'OPTICS';
        if (normalizedValue.includes('sun')) return 'SUN';
        if (normalizedValue.includes('afa')) return 'AFA';
        if (normalizedValue.includes('vas')) return 'VAS';

        return 'EYEWEAR';
    }

    getTealiumProductEntry() {
        if (typeof window.poResolveTealiumProductEntry === 'function') {
            const resolvedEntry = window.poResolveTealiumProductEntry({
                productHandle: this.wishlistItem.productHandle,
                variantSku: this.wishlistItem.variantSku,
                productId: this.wishlistItem.productId,
                variantId: this.wishlistItem.variantId,
                productData: this.getDatasetTealiumProductData()
            });

            if (resolvedEntry?.key) {
                return resolvedEntry;
            }
        }

        const tealiumProducts = window.tealium?.products;
        if (!tealiumProducts || typeof tealiumProducts !== 'object') {
            return null;
        }

        const currentProductId = window.tealium?.currentProductId || '';
        const currentProduct = currentProductId ? tealiumProducts[currentProductId] : null;
        const directProductKey = this.getDirectTealiumProductKeyCandidate();
        const firstProductEntry = Object.entries(tealiumProducts).find(([, product]) => (
            product && typeof product === 'object'
        )) || null;
        if (directProductKey && tealiumProducts[directProductKey]) {
            return {
                key: directProductKey,
                data: tealiumProducts[directProductKey]
            };
        }

        if (currentProductId && currentProduct) {
            return {
                key: currentProductId,
                data: currentProduct
            };
        }

        const matchedEntry = Object.entries(tealiumProducts).find(([, product]) => {
            const handle = String(product?.Handle || product?.handle || '').trim().toLowerCase();
            return handle && handle === String(this.wishlistItem.productHandle || '').trim().toLowerCase();
        });

        if (!matchedEntry) {
            if (!firstProductEntry) {
                return null;
            }

            return {
                key: firstProductEntry[0],
                data: firstProductEntry[1]
            };
        }

        return {
            key: matchedEntry[0],
            data: matchedEntry[1]
        };
    }

    getTealiumPriceFields(eventKey = WISHLIST_CONSTANTS.eventKeyAdd) {
        const productPrice = window.opTealiumPrices?.find(
            (item) => item.productHandle === this.wishlistItem.productHandle
        );
        const tealiumProduct = this.getTealiumProductEntry()?.data || this.getDatasetTealiumProductData();
        const priceFields = tealiumProduct && typeof tealiumProduct === 'object'
            ? { ...tealiumProduct }
            : {};

        const formatCentsPrice = (value) => {
            const numericValue = Number(value);
            return Number.isFinite(numericValue) ? (numericValue / 100).toFixed(2) : '';
        };

        if (productPrice?.price !== undefined) {
            priceFields.Price = formatCentsPrice(productPrice.price);
            priceFields.PriceNet = formatCentsPrice(productPrice.price);
        } else if (tealiumProduct?.Price) {
            priceFields.Price = String(tealiumProduct.Price);
            if (tealiumProduct?.PriceNet) {
                priceFields.PriceNet = String(tealiumProduct.PriceNet);
            }
        }

        if (productPrice?.priceFull !== undefined) {
            priceFields.PriceFull = formatCentsPrice(productPrice.priceFull);
            priceFields.PriceFullNet = formatCentsPrice(productPrice.priceFull);
        } else if (tealiumProduct?.PriceFull) {
            priceFields.PriceFull = String(tealiumProduct.PriceFull);
            if (tealiumProduct?.PriceFullNet) {
                priceFields.PriceFullNet = String(tealiumProduct.PriceFullNet);
            }
        }

        if (!priceFields.LensType) {
            priceFields.LensType = 'PLANO';
        }

        if (priceFields.Category) {
            priceFields.Category = this.normalizeTealiumCategory(priceFields.Category);
        }

        if (eventKey === WISHLIST_CONSTANTS.eventKeyAdd) {
            delete priceFields.PriceNet;
            delete priceFields.PriceFullNet;
        }

        return priceFields;
    }

    sendTealiumTracking(eventKey = WISHLIST_CONSTANTS.eventKeyAdd) {
        const productKey = this.getTealiumProductKey();
        if (!productKey) return;

        const tealiumEventFlag = eventKey === WISHLIST_CONSTANTS.eventKeyRemove
            ? 'Events_ProdFavRem'
            : 'Events_ProdFavAdd';

        const tealiumEvent = {
            id: 'Event',
            [tealiumEventFlag]: '1',
            Products: {
                [productKey]: this.getTealiumPriceFields(eventKey),
            },
        };

        if (typeof window.sendTealiumEvent === 'function') {
            window.sendTealiumEvent(tealiumEvent);
        } else if (typeof window.tealium_data2track !== 'undefined' && typeof window.tealium_data2track.push === 'function') {
            window.tealium_data2track.push({ ...tealiumEvent });
        }
    }

    handleKeydown(event) {
        if (document.activeElement !== this) return;

        if (['Enter', ' ', 'Spacebar'].includes(event.key)) {
            event.preventDefault();
            this.click();
        }
    }

    async checkWishlistItemPresence() {
        this.isInWishlist = await PoWishlistUtils.isItemInWishlist(this.wishlistItem.variantId);
        this.toggleAttribute('active', this.isInWishlist);
        this.syncAriaPressed();
    }

    async syncWishlist() {
        const wl = await PoWishlistUtils.retrieveWishlist();
        this.isInWishlist = wl.some(item => item.variant_id === this.wishlistItem.variantId);
        this.toggleAttribute('active', this.isInWishlist);
        this.syncAriaPressed();
    }

}

if (!customElements.get('po-wishlist-toggle-button')) {
    customElements.define('po-wishlist-toggle-button', PoWishlistToggleButton);
}

class PoWishlistHeaderIcon extends HTMLElement {
    constructor() {
        super();
        this.url = this.dataset.url || '#';
        this.unsubscribe = null;
    }

    async connectedCallback() {
       await this.syncWishlist();
    
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.updateWishlist, ({ wishlist }) => {
            this.setAttribute('active', wishlist.length > 0);
        });

        this.addEventListener('click', () => {
            window.location = this.url;
        });
    }

    disconnectedCallback() {
        this.unsubscribe?.();
    }

    async syncWishlist() {
        const wl = await PoWishlistUtils.retrieveWishlist();
        this.setAttribute('active', wl.length > 0);
    }
}

if (!customElements.get('po-wishlist-header-icon')) {
    customElements.define('po-wishlist-header-icon', PoWishlistHeaderIcon);
}

// bfcache (back-forward cache)
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  
  document.querySelectorAll('po-wishlist-toggle-button').forEach(btn => btn.syncWishlist());
  document.querySelectorAll('po-wishlist-header-icon').forEach(icon => icon.syncWishlist());
});
